import { SERVICES_TAGS } from 'constants/servicesTags';
import initialApiService from 'services/initialApiService';

import { PROMOTER_DEFAULT_SETTING_TYPE_ENUM } from 'types/core/systemTypes';
import {
  EventOfferCreateReqType,
  EventOfferResType,
  EventScheduleReqType,
  EventScheduleResType,
  EventSingleOfferResType,
  EventSingleScheduleResType,
  PromoterFullInfoType,
  PromoterSettingItem,
  ReferralLinkResType,
  ReferrerGroupEditReqType,
  ReferrerGroupReqType,
  ReferrerGroupResType,
  ReferrerItemResType,
  ReferrerLinkReqType,
  ReferrerReqType,
  ReferrerResType,
  WebHookCreateReqType,
  WebHookResType,
  WebHookSingleResType,
} from 'types/promoters/promoterTypes';

const promoterApiService = initialApiService.injectEndpoints({
  endpoints: (builder) => ({
    getPromoterInfo: builder.query<PromoterFullInfoType, string>({
      query: (id) => ({
        url: `/promoters/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: SERVICES_TAGS.PROMOTER_FULL_INFO, id }],
    }),
    editPromoterInfo: builder.mutation<PromoterFullInfoType, PromoterFullInfoType>({
      query: (data) => ({
        url: `/promoters/${data.id}/edit`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: SERVICES_TAGS.PROMOTER_FULL_INFO, id: arg.id },
        SERVICES_TAGS.UPDATE_PROMOTER,
      ],
    }),
    createPromoter: builder.mutation<PromoterFullInfoType, PromoterFullInfoType>({
      query: (data) => ({
        url: `/promoters`,
        method: 'POST',
        body: data,
      }),
      // Assuming a general list tag 'PromoterList' might exist or be added elsewhere for listing promoters
      invalidatesTags: (result) =>
        result
          ? [
              { type: SERVICES_TAGS.PROMOTER_FULL_INFO, id: result.id },
              SERVICES_TAGS.UPDATE_PROMOTER,
              'PromoterList',
            ]
          : [SERVICES_TAGS.UPDATE_PROMOTER, 'PromoterList'],
    }),
    uploadLogo: builder.mutation<PromoterFullInfoType, { promoterId: string; logo: string }>({
      query: (data) => ({
        url: `/promoters/logo`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: SERVICES_TAGS.PROMOTER_FULL_INFO, id: arg.promoterId },
        SERVICES_TAGS.UPDATE_PROMOTER,
      ],
    }),
    deleteLogo: builder.mutation<PromoterFullInfoType, { promoterId: string }>({
      query: ({ promoterId }) => ({
        url: `/promoters/${promoterId}/logo`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: SERVICES_TAGS.PROMOTER_FULL_INFO, id: arg.promoterId },
        SERVICES_TAGS.UPDATE_PROMOTER,
      ],
    }),
    createReferrerLink: builder.mutation<ReferralLinkResType, ReferrerLinkReqType>({
      query: (data) => ({
        url: `/referrers/createreferrerlink`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) =>
        arg.promoterId ? [{ type: 'ReferrersList', id: arg.promoterId }] : ['ReferrersList'],
    }),
    editReferrerLink: builder.mutation<ReferralLinkResType, ReferrerLinkReqType>({
      query: (data) => ({
        url: `/referrers/referrerlink`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ReferrerItem', id: arg.promoterId },
        arg.promoterId ? { type: 'ReferrersList', id: arg.promoterId } : 'ReferrersList',
      ],
    }),
    createReferrersGroup: builder.mutation<ReferrerGroupResType, ReferrerGroupReqType>({
      query: (data) => ({
        url: `/referrers/group`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) =>
        arg.promoterId
          ? [{ type: 'ReferrerGroup', id: 'LIST', promoterId: arg.promoterId }]
          : ['ReferrerGroupList'],
    }),
    editReferrersGroup: builder.mutation<ReferrerGroupResType, ReferrerGroupEditReqType>({
      query: (data) => ({
        url: `/referrers/group`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ReferrerGroup', id: arg.id },
        arg.promoterId
          ? { type: 'ReferrerGroup', id: 'LIST', promoterId: arg.promoterId }
          : 'ReferrerGroupList',
      ],
    }),
    deleteReferrersGroup: builder.mutation<string, { id?: string; promoterId?: string }>({
      // Assuming promoterId might be passed or known
      query: (params) => ({
        url: `/referrers/group`,
        method: 'DELETE',
        params: { id: params.id },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ReferrerGroup', id: arg.id },
        arg.promoterId
          ? { type: 'ReferrerGroup', id: 'LIST', promoterId: arg.promoterId }
          : 'ReferrerGroupList',
      ],
    }),
    getReferrersGroups: builder.query<ReferrerGroupResType[], { id?: string }>({
      // id here is promoterId
      query: (params) => ({
        url: `/referrers/groups`,
        method: 'GET',
        params,
      }),
      providesTags: (result, error, args) => [
        ...(result?.map(({ id }) => ({ type: 'ReferrerGroup', id } as const)) || []),
        { type: 'ReferrerGroup', id: 'LIST', promoterId: args.id },
      ],
    }),
    getReferrersList: builder.query<ReferrerResType, ReferrerReqType>({
      // ReferrerReqType includes promoterId
      query: (params) => ({
        url: `/referrers/promoterreferrers`,
        method: 'GET',
        params,
      }),
      providesTags: (result, error, args) => [{ type: 'ReferrersList', id: args.PromoterId }],
    }),
    getReferrerItem: builder.query<ReferrerItemResType, { id?: string }>({
      query: (params) => ({
        url: `/referrers/referrerlink`,
        method: 'GET',
        params,
      }),
      providesTags: (result, error, args) =>
        args.id ? [{ type: 'ReferrerItem', id: args.id }] : [],
    }),
    deleteReferrerItem: builder.mutation<string, { id?: string; promoterId?: string }>({
      // Assuming promoterId might be passed
      query: (params) => ({
        url: `/referrers/referrerlink`,
        method: 'DELETE',
        params,
      }),
    }),
    createOffer: builder.mutation<EventOfferResType, EventOfferCreateReqType>({
      query: (data) => ({
        url: `/events/offer`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EventOffers'],
    }),
    getOffers: builder.query<EventOfferResType, { eventId?: string }>({
      query: ({ eventId }) => ({
        url: `/events/offers/${eventId}`,
      }),
      providesTags: ['EventOffers'],
    }),
    getOffer: builder.query<EventSingleOfferResType, { offerId?: string }>({
      query: ({ offerId }) => ({
        url: `/events/offer/${offerId}`,
      }),
      providesTags: ['EventOffers'],
    }),
    updateOffer: builder.mutation<EventOfferResType, { id: string; data: EventOfferCreateReqType }>(
      {
        query: ({ id, data }) => ({
          url: `/events/${id}/offer`,
          method: 'PATCH',
          body: data,
        }),
        invalidatesTags: (result, error, { id }) => [{ type: 'EventOffers', id }],
      },
    ),
    deleteOffer: builder.mutation<EventOfferResType, string>({
      query: (id) => ({
        url: `/events/${id}/offer`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'EventOffers', id }],
    }),
    deleteOfferTicketType: builder.mutation<EventSingleOfferResType, string>({
      query: (id) => ({
        url: `/events/${id}/offertickettype`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'EventOffers', id }],
    }),
    createWebHook: builder.mutation<WebHookResType, WebHookCreateReqType>({
      query: (data) => ({
        url: `/events/webhook`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['webhook'],
    }),
    getWebHooks: builder.query<WebHookResType, { eventId?: string }>({
      query: ({ eventId }) => ({
        url: `/events/webhooks/${eventId}`,
      }),
      providesTags: ['webhook'],
    }),
    getWebHook: builder.query<WebHookSingleResType, { hookId?: string }>({
      query: ({ hookId }) => ({
        url: `/events/webhook/${hookId}`,
      }),
      providesTags: ['webhook'],
    }),
    updateWebHook: builder.mutation<WebHookResType, { id: string; data: WebHookCreateReqType }>({
      query: ({ id, data }) => ({
        url: `/events/${id}/webhook`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'webhook', id }],
    }),
    deleteWebHook: builder.mutation<WebHookResType, string>({
      query: (id) => ({
        url: `/events/${id}/webhook`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'webhook', id }],
    }),
    createPromoterSettings: builder.mutation<
      PromoterFullInfoType,
      { id: string; settings: PromoterSettingItem[] }
    >({
      query: ({ id, settings }) => ({
        url: `/promoters/${id}/defaultsettings`,
        method: 'POST',
        body: { settings },
      }),
      invalidatesTags: ['promoterSettings'],
    }),
    deletePromoterSettings: builder.mutation<
      PromoterFullInfoType,
      { id: string; type: PROMOTER_DEFAULT_SETTING_TYPE_ENUM }
    >({
      query: ({ id, type }) => ({
        url: `/promoters/${id}/defaultsettings`,
        method: 'DELETE',
        params: { type },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'promoterSettings', id }],
    }),
    createSchedule: builder.mutation<EventScheduleResType, EventScheduleReqType>({
      query: (data) => ({
        url: `/events/schedule`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EventSchedules'],
    }),
    getSchedules: builder.query<EventScheduleResType, { eventId?: string }>({
      query: ({ eventId }) => ({
        url: `/events/schedules/${eventId}`,
      }),
      providesTags: ['EventSchedules'],
    }),
    getSchedule: builder.query<EventSingleScheduleResType, { id?: string }>({
      query: ({ id }) => ({
        url: `/events/schedule/${id}`,
      }),
      providesTags: ['EventSchedules'],
    }),
    updateSchedule: builder.mutation<
      EventScheduleResType,
      { id: string; data: EventScheduleReqType }
    >({
      query: ({ id, data }) => ({
        url: `/events/${id}/schedule`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'EventSchedules', id }],
    }),
    deleteSchedule: builder.mutation<EventScheduleResType, string>({
      query: (id) => ({
        url: `/events/schedule/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'EventSchedules', id }],
    }),
  }),
});

export const {
  useLazyGetPromoterInfoQuery,
  useEditPromoterInfoMutation,
  useUploadLogoMutation,
  useDeleteLogoMutation,
  useCreatePromoterMutation,
  useCreateReferrerLinkMutation,
  useLazyGetReferrersListQuery,
  useCreateReferrersGroupMutation,
  useLazyGetReferrersGroupsQuery,
  useLazyGetReferrerItemQuery,
  useEditReferrerLinkMutation,
  useDeleteReferrerItemMutation,
  useEditReferrersGroupMutation,
  useDeleteReferrersGroupMutation,
  useCreateOfferMutation,
  useLazyGetOfferQuery,
  useLazyGetOffersQuery,
  useDeleteOfferMutation,
  useUpdateOfferMutation,
  useDeleteOfferTicketTypeMutation,
  useCreateWebHookMutation,
  useLazyGetWebHookQuery,
  useLazyGetWebHooksQuery,
  useUpdateWebHookMutation,
  useDeleteWebHookMutation,
  useCreatePromoterSettingsMutation,
  useDeletePromoterSettingsMutation,
  useCreateScheduleMutation,
  useLazyGetScheduleQuery,
  useLazyGetSchedulesQuery,
  useDeleteScheduleMutation,
  useUpdateScheduleMutation,
} = promoterApiService;
